/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  InjectionToken,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { WINDOW } from '@myprofile-ui/pidx-utils'
import { TranslateParser } from '@ngx-translate/core'

export const TIMER_DURATION = new InjectionToken<number>('TIMER_DURATION')

@Component({
  selector: 'pidx-timer-label',
  templateUrl: './pidx-timer-label.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PidxTimerLabelComponent implements OnInit, OnDestroy {
  protected window = inject<Window>(WINDOW)
  private translateParser = inject(TranslateParser)
  private cdr = inject(ChangeDetectorRef)

  @Input() waitingTimerLabelText!: string
  @Input() requestCodeTimerLabel!: string
  /** Resolved (already translated) tooltip text for the info popover. When empty, the popover is not rendered. */
  @Input() tooltipInfoText = ''
  @Input() timer = 30
  /** When `true`, the resend button is rendered disabled and its click is ignored (e.g. backend throttling). */
  @Input() disabled = false

  @Output() clickedTimerEvent = new EventEmitter<void>()

  public isEnabled = false

  private _timeRemaining = 30
  protected set timeRemaining(newValue: any) {
    this._timeRemaining = newValue
  }

  protected get timeRemaining() {
    return { TIME_REMAINING: this._timeRemaining }
  }

  protected counter: any
  protected timerLabel = ''
  /**
   * Polite live-region text. Updated only at meaningful moments (start of the
   * countdown and once it elapses) so screen-reader users are not flooded with
   * an announcement every second (WCAG 4.1.3 Status Messages).
   */
  protected announcement = ''

  public currentTime!: number

  ngOnInit() {
    this.currentTime = this.timer
    this.start()
  }

  public clickHandler() {
    if (this.disabled) {
      return
    }
    this.isEnabled = false
    this.clickedTimerEvent.emit()
    this.timeRemaining = this.currentTime
    this.start()
    this.cdr.markForCheck()
  }

  private updateTimerLabel() {
    this.timerLabel = this.translateParser.interpolate(this.waitingTimerLabelText, this.timeRemaining) ?? ''
  }

  public ngOnDestroy(): void {
    if (this.counter !== undefined) {
      clearInterval(this.counter)
      this.counter = undefined
    }
  }

  private start() {
    if (this.counter !== undefined) {
      clearInterval(this.counter)
      this.counter = undefined
    }

    this.updateTimerLabel()
    this.announcement = this.timerLabel
    this.currentTime--

    this.counter = this.window.setInterval(() => {
      this.timeRemaining = this.currentTime
      this.updateTimerLabel()

      this.currentTime--

      if (this.currentTime === -1) {
        clearInterval(this.counter)
        this.counter = undefined
        this.isEnabled = true
        this.announcement = ''

        this.currentTime = this.timer
      }

      this.cdr.markForCheck()
    }, 1000)
  }
}
